import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { Tooltip } from 'react-tooltip';

// focusable false: the button is not in the Tab order (a copy for the eye, inside an aria-hidden part; the real one is focusable).
type Props = { label: string, title: string, children: React.ReactNode, focusable?: boolean };

const InfoTooltip: React.FunctionComponent<Props> = ({ label, title, children, focusable = true }) => {
	const id = `info-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const [isOpen, setIsOpen] = React.useState(false);
	const closeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const cancelClose = () => clearTimeout(closeTimer.current);
	const open = () => { cancelClose(); setIsOpen(true); };
	const close = () => { cancelClose(); setIsOpen(false); };
	const closeAfterPointerExit = () => {
		cancelClose();
		closeTimer.current = setTimeout(() => setIsOpen(false), 150);
	};

	React.useEffect(() => () => clearTimeout(closeTimer.current), []);
	React.useEffect(() => {
		if (!isOpen) return;
		const closeOutside = (event: PointerEvent) => {
			if (!(event.target instanceof Node)) return;
			if (document.getElementById(id)?.contains(event.target) === true) return;
			if (document.getElementById(`${id}-tooltip`)?.contains(event.target) === true) return;
			clearTimeout(closeTimer.current);
			setIsOpen(false);
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			clearTimeout(closeTimer.current);
			setIsOpen(false);
		};
		document.addEventListener('pointerdown', closeOutside, true);
		document.addEventListener('keydown', closeOnEscape, true);
		return () => {
			document.removeEventListener('pointerdown', closeOutside, true);
			document.removeEventListener('keydown', closeOnEscape, true);
		};
	}, [id, isOpen]);

	return <>
		<button id={id} type="button" aria-label={label} tabIndex={focusable ? undefined : -1}
			aria-describedby={isOpen ? `${id}-tooltip` : undefined}
			onMouseEnter={open} onMouseLeave={closeAfterPointerExit}
			onFocus={open} onBlur={close} onClick={open}
			className="relative group inline-flex shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
			<InformationCircleIcon aria-hidden="true" className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-500" />
		</button>
		{isOpen && <Tooltip id={`${id}-tooltip`} anchorSelect={`#${id}`} place="top"
			positionStrategy="fixed" clickable isOpen={isOpen}
			openEvents={{}} closeEvents={{}} globalCloseEvents={{}}
			style={{ maxWidth: 'calc(100vw - 16px)', whiteSpace: 'normal' }}
			className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5">
			<div onMouseEnter={cancelClose} onMouseLeave={closeAfterPointerExit}
				className="w-72 max-w-full rounded-lg bg-white px-4 py-3 text-xs text-gray-600">
				<p className="text-sm font-semibold text-gray-900">{title}</p>
				<p className="mt-2 border-t border-gray-200 pt-2 leading-5">{children}</p>
			</div>
		</Tooltip>}
	</>;
};

export default InfoTooltip;
